import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, UserCheck, Loader2, AlertTriangle, Wrench, LogIn, CheckCircle, User, X, Smartphone, Monitor, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction pour détecter l'appareil/navigateur
const getDeviceInfo = () => {
  const ua = navigator.userAgent.toLowerCase();
  
  if (/iphone|ipad|ipod/.test(ua)) {
    return {
      type: 'ios',
      name: 'iPhone/iPad',
      icon: '📱',
      instructions: [
        'Ouvrez l\'app "Réglages" de votre iPhone',
        'Allez dans "Confidentialité et sécurité"',
        'Appuyez sur "Service de localisation"',
        'Vérifiez que le service est activé (en vert)',
        'Descendez et trouvez "Safari" (ou votre navigateur)',
        'Sélectionnez "Lorsque l\'app est active"'
      ]
    };
  }
  
  if (/android/.test(ua)) {
    if (/samsung/.test(ua)) {
      return {
        type: 'android-samsung',
        name: 'Samsung',
        icon: '📱',
        instructions: [
          'Appuyez sur l\'icône 🔒 dans la barre d\'adresse',
          'Appuyez sur "Autorisations"',
          'Activez "Position"',
          'Ou allez dans Paramètres > Applications > Navigateur > Autorisations > Position'
        ]
      };
    }
    return {
      type: 'android',
      name: 'Android',
      icon: '📱',
      instructions: [
        'Appuyez sur l\'icône 🔒 à gauche de l\'URL',
        'Appuyez sur "Autorisations"',
        'Activez "Position"',
        'Ou : Paramètres > Position > activer le GPS'
      ]
    };
  }
  
  if (/firefox/.test(ua)) {
    return {
      type: 'firefox',
      name: 'Firefox',
      icon: '🦊',
      instructions: [
        'Cliquez sur l\'icône 🔒 à gauche de l\'URL',
        'Cliquez sur "Connexion sécurisée"',
        'Cliquez sur "Plus d\'informations"',
        'Onglet "Permissions" > Position > Autoriser'
      ]
    };
  }
  
  if (/safari/.test(ua) && !/chrome/.test(ua)) {
    return {
      type: 'safari',
      name: 'Safari (Mac)',
      icon: '🧭',
      instructions: [
        'Cliquez sur Safari dans la barre de menu',
        'Sélectionnez "Préférences"',
        'Allez dans l\'onglet "Sites web"',
        'Cliquez sur "Position" dans la liste de gauche',
        'Autorisez ce site'
      ]
    };
  }
  
  // Chrome ou autre
  return {
    type: 'chrome',
    name: 'Chrome',
    icon: '💻',
    instructions: [
      'Cliquez sur l\'icône 🔒 à gauche de l\'URL',
      'Cliquez sur "Paramètres du site"',
      'Trouvez "Position"',
      'Sélectionnez "Autoriser"',
      'Rechargez la page si nécessaire'
    ]
  };
};

export default function PointagePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('login'); // 'login', 'confirm', 'success'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState({ enabled: false, reason: '', estimated_end: '' });
  const [gpsAccepted, setGpsAccepted] = useState(false);
  const [gpsErrorModal, setGpsErrorModal] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  
  // Secteur depuis l'URL (QR Code)
  const [sectorFromQr, setSectorFromQr] = useState(null);
  const [sectorInfo, setSectorInfo] = useState(null); // Info du secteur QR
  
  // User data from login
  const [userData, setUserData] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  
  useEffect(() => {
    fetchMaintenanceStatus();
    
    // Vérifier si un secteur est passé dans l'URL (scan QR Code)
    const sectorId = searchParams.get('sector');
    if (sectorId) {
      setSectorFromQr(sectorId);
      console.log('Secteur depuis QR Code:', sectorId);
      // Récupérer les infos du secteur
      fetchSectorInfo(sectorId);
    }
  }, [searchParams]);
  
  const fetchSectorInfo = async (sectorId) => {
    try {
      const response = await axios.get(`${API}/secteur-public/${sectorId}`);
      setSectorInfo(response.data);
      console.log('Info secteur QR:', response.data);
    } catch (error) {
      console.error('Erreur récupération secteur:', error);
    }
  };
  
  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API}/maintenance`);
      setMaintenanceMode(response.data);
    } catch (error) {
      console.error('Erreur chargement maintenance:', error);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/scan-login`, {
        username: username.trim(),
        password: password
      });
      
      const data = response.data;
      setUserData(data);
      
      // If admin, redirect to dashboard
      if (data.is_admin) {
        // Store token for admin session
        localStorage.setItem('token', data.token);
        localStorage.setItem('username', data.username);
        toast.success('Connexion réussie !');
        navigate('/admin/dashboard');
        return;
      }
      
      // If not admin, go to confirmation step
      // Autoriser si: secteur QR code OU secteur lié au compte
      if (!data.site_id && !sectorFromQr) {
        toast.error('Aucun site associé à votre compte. Scannez un QR code pour accéder à un secteur.');
        setLoading(false);
        return;
      }
      
      setStep('confirm');
      setPassword(''); // Clear password immediately for security
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmScan = async () => {
    if (!gpsAccepted) {
      toast.error('La position GPS est obligatoire pour valider le scan');
      return;
    }
    
    // Déterminer quel site utiliser: QR Code prioritaire, sinon site de l'utilisateur
    const siteIdToUse = sectorFromQr || userData.site_id;
    
    if (!siteIdToUse) {
      toast.error('Aucun secteur défini');
      return;
    }
    
    setLoading(true);
    
    try {
      // Get GPS position - OBLIGATOIRE
      let position;
      try {
        position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            (error) => reject(error),
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
          );
        });
      } catch (gpsError) {
        // Afficher le popup d'aide GPS avec instructions selon l'appareil
        setDeviceInfo(getDeviceInfo());
        setGpsErrorModal(true);
        setLoading(false);
        return;
      }
      
      // Vérifier que la position est valide
      if (!position.lat || !position.lng) {
        setDeviceInfo(getDeviceInfo());
        setGpsErrorModal(true);
        setLoading(false);
        return;
      }
      
      // Create pointage avec le secteur du QR code
      const response = await axios.post(`${API}/scan-pointage`, {
        site_id: siteIdToUse,
        user_display_name: userData.display_name,
        gps_lat: position.lat,
        gps_lng: position.lng
      });
      
      const result = response.data;
      
      // Handle "already scanned" case
      if (result.status === 'already_scanned') {
        setScanResult({ 
          scan_type: 'already_scanned', 
          message: result.message || "Votre portail est déjà ouvert" 
        });
        setStep('success');
        setTimeout(() => resetForm(), 3000);
        return;
      }
      
      setScanResult(result);
      setStep('success');
      
      // Auto reset after 3 seconds
      setTimeout(() => {
        resetForm();
      }, 3000);
    } catch (error) {
      console.error('Pointage error:', error);
      toast.error('Erreur lors du scan');
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setStep('login');
    setUsername('');
    setPassword('');
    setUserData(null);
    setGpsAccepted(false);
    setScanResult(null);
  };
  
  // Maintenance mode display
  if (maintenanceMode.enabled) {
    const formatEstimatedEnd = (dateStr) => {
      if (!dateStr) return null;
      try {
        const date = new Date(dateStr);
        return date.toLocaleString('fr-FR', { 
          dateStyle: 'long', 
          timeStyle: 'short' 
        });
      } catch {
        return dateStr;
      }
    };
    
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: document.documentElement.classList.contains('dark') ? '#141c2b' : '#f8f9fa'
      }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_8273b7de-0616-4daf-8063-852b843c4c40/artifacts/w2af3asz_logo-final-couleur.png" 
              alt="My Open Close Logo" 
              className="h-24 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>My Open Close</h1>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-6 text-center"
          >
            <div 
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ background: '#f59e0b20' }}
            >
              <Wrench className="h-8 w-8" style={{ color: '#f59e0b' }} />
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Maintenance en cours
            </h2>
            
            <p className="text-gray-600 dark:text-zinc-400 mb-4">
              Le service est temporairement indisponible pour maintenance.
            </p>
            
            <div 
              className="p-4 rounded-lg mb-4 text-left"
              style={{ 
                background: document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6'
              }}
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">Raison</p>
                  <p className="text-gray-600 dark:text-zinc-400 text-sm">{maintenanceMode.reason}</p>
                </div>
              </div>
              
              {maintenanceMode.estimated_end && (
                <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <Clock className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#5BA4CF]" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">Fin estimée</p>
                    <p className="text-gray-600 dark:text-zinc-400 text-sm">{formatEstimatedEnd(maintenanceMode.estimated_end)}</p>
                  </div>
                </div>
              )}
            </div>
            
          </motion.div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 gradient-bg" style={{
      background: document.documentElement.classList.contains('dark') ? '#141c2b' : '#f8f9fa'
    }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_8273b7de-0616-4daf-8063-852b843c4c40/artifacts/w2af3asz_logo-final-couleur.png" 
            alt="My Open Close Logo" 
            className="h-24 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>My Open Close</h1>
          <p className="text-gray-600 dark:text-zinc-400 mt-2">Opening & Closing Management</p>
        </div>
        
        {/* Step 1: Login Form */}
        {step === 'login' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-6 space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Connexion
              </h2>
              <div className="px-3 py-1 rounded-full text-sm font-semibold bg-[#5BA4CF]/20 text-[#5BA4CF]">
                <LogIn className="inline h-4 w-4 mr-1" />
                SCAN
              </div>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-gray-700 dark:text-zinc-300 mb-2 block">
                  Identifiant Prysma
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 focus:ring-2 focus:ring-[#5BA4CF] text-gray-900 dark:text-white"
                  style={{
                    background: document.documentElement.classList.contains('dark') ? '#141c2b' : '#f8f9fa',
                    borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'
                  }}
                  placeholder="nom.prenom"
                  autoComplete="username"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="text-gray-700 dark:text-zinc-300 mb-2 block">
                  Mot de passe
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 focus:ring-2 focus:ring-[#5BA4CF] text-gray-900 dark:text-white"
                  style={{
                    background: document.documentElement.classList.contains('dark') ? '#141c2b' : '#f8f9fa',
                    borderColor: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'
                  }}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#5BA4CF] hover:bg-[#4A93BE] text-white font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-5 w-5" />
                    Se connecter
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        )}
        
        {/* Step 2: Confirm Scan (Employee only) */}
        {step === 'confirm' && userData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-6 space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Confirmation
              </h2>
              <div className="px-3 py-1 rounded-full text-sm font-semibold bg-[#5BA4CF]/20 text-[#5BA4CF]">
                <UserCheck className="inline h-4 w-4 mr-1" />
                SCAN
              </div>
            </div>
            
            {/* User Info Card */}
            <div 
              className="p-4 rounded-lg"
              style={{
                background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#f3f4f6'
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: '#5BA4CF20' }}
                >
                  <User className="h-6 w-6 text-[#5BA4CF]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{userData.display_name}</p>
                  <p className="text-sm text-gray-500 dark:text-zinc-400">@{userData.username}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-[#5BA4CF]" />
                <span className="text-gray-700 dark:text-zinc-300">
                  {sectorFromQr && sectorInfo 
                    ? sectorInfo.nom 
                    : (userData.site_name || userData.secteur_name || 'Site non défini')}
                </span>
                {sectorFromQr && sectorInfo && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    QR Code
                  </span>
                )}
              </div>
            </div>
            
            {/* GPS Checkbox */}
            <div 
              className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
              style={{
                background: gpsAccepted 
                  ? (document.documentElement.classList.contains('dark') ? '#10b98120' : '#10b98110')
                  : (document.documentElement.classList.contains('dark') ? '#374151' : '#f3f4f6'),
                border: gpsAccepted ? '1px solid #10b981' : '1px solid transparent'
              }}
              onClick={() => setGpsAccepted(!gpsAccepted)}
            >
              <div 
                className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                  gpsAccepted ? 'bg-green-500' : 'border-2 border-gray-400 dark:border-zinc-500'
                }`}
              >
                {gpsAccepted && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${gpsAccepted ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-zinc-300'}`}>
                  J'accepte le partage de ma position GPS
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
                  <MapPin className="h-3 w-3 inline mr-1" />
                  Obligatoire pour valider le scan
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={resetForm}
                variant="outline"
                className="flex-1 h-12"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmScan}
                disabled={loading || !gpsAccepted}
                className="flex-1 h-12 bg-[#5BA4CF] hover:bg-[#4A93BE] text-white font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Validation...
                  </>
                ) : (
                  <>
                    <UserCheck className="mr-2 h-5 w-5" />
                    Valider
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
        
        {/* Step 3: Success */}
        {step === 'success' && scanResult && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-xl p-8 text-center"
          >
            <div 
              className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ 
                background: scanResult.status === 'already_scanned' || scanResult.scan_type === 'already_scanned'
                  ? '#f59e0b20' 
                  : scanResult.status === 'hors_marge' 
                    ? '#6b728020'
                    : '#10b98120' 
              }}
            >
              {scanResult.status === 'already_scanned' || scanResult.scan_type === 'already_scanned' ? (
                <Clock className="h-10 w-10 text-amber-500" />
              ) : scanResult.status === 'hors_marge' ? (
                <UserCheck className="h-10 w-10 text-gray-500" />
              ) : (
                <CheckCircle className="h-10 w-10 text-green-500" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {scanResult.status === 'already_scanned' || scanResult.scan_type === 'already_scanned'
                ? 'Déjà enregistré'
                : scanResult.scan_type === 'fermeture' 
                  ? 'Fermeture validée !'
                  : 'Ouverture validée !'}
            </h2>
            
            <p className="text-gray-600 dark:text-zinc-400 text-center">
              {scanResult.message}
            </p>
            
            <p className="text-sm text-gray-500 dark:text-zinc-500 mt-4">
              Retour automatique dans quelques secondes...
            </p>
          </motion.div>
        )}
      </div>
      
      {/* Modal d'aide GPS */}
      <Dialog open={gpsErrorModal} onOpenChange={setGpsErrorModal}>
        <DialogContent className="max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-red-500" />
              </div>
              Position GPS requise
            </DialogTitle>
          </DialogHeader>
          
          {deviceInfo && (
            <div className="space-y-4 mt-2">
              {/* Appareil détecté */}
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <span className="text-2xl">{deviceInfo.icon}</span>
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Appareil détecté : {deviceInfo.name}
                </span>
              </div>
              
              {/* Instructions */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Pour activer le GPS, suivez ces étapes :
                </p>
                <ol className="space-y-2">
                  {deviceInfo.instructions.map((instruction, index) => (
                    <li 
                      key={index}
                      className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#5BA4CF] text-white flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="pt-0.5">{instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>
              
              {/* Boutons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setGpsErrorModal(false)}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Fermer
                </Button>
                <Button
                  onClick={() => {
                    setGpsErrorModal(false);
                    // Réessayer le scan
                    handleConfirmScan();
                  }}
                  className="flex-1 bg-[#5BA4CF] hover:bg-[#4a93be]"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
